import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from "react";
import { currencySymbol } from "../../custom/getCurrencySymbol";
import { ExtendedFieldMetaDataTypeOptional } from "../../dynamicForm";

export interface CustomProperties {
  /**
   * display _accountNumber field as single field or separated
   * @default "separated"
   */
  dynamicAccountNumberField?: "single" | "separated";
  /**
   * sets default currency symbol
   * For example: INR -> ₹
   * @default "INR"
   */
  dynamicAmountSymbol?: keyof typeof currencySymbol;
  /**
   * sets default amount group style for amount fields
   * works for form fields
   * @default "lakh"
   */
  dynamicAmountGroupStyle?: "lakh" | "thousand" | "wan" | "none";
  /**
   * sets default currency format for gridWrapper
   * @default "en-IN"
   */
  currencyFormat?: string; //for report set currency format
  /**
   * sets default decimal places for number format fields
   * @default 2
   */
  decimalCount?: number; //for report set decimal count
  /**
   * sets default date format for datePicker fields
   * @default "dd/MM/yyyy"
   */
  commonDateFormat?: string;
  /**
   * sets default dateTime format for dateTimePicker fields
   * @default "dd/MM/yyyy hh:mm:ss aa"
   */
  commonDateTimeFormat?: string;
  /**
   * sets default time format for timePicker fields
   * @default "hh:mm:ss aa"
   */
  commonTimeFormat?: string;
  /**
   * sets default denomination table type
   * @default "single"
   */
  denoTableType?: "single" | "dual";
  /**
   * Accepts Field MetaData object for additional form fields
   */
  customExtendedTypes?: ExtendedFieldMetaDataTypeOptional;
  /**
   * UI Configuration for gridwrapper
   */
  defaultGridConfig?: {
    /**
     * Specify the grid style to appear
     * @default "standard"
     */
    variant?: "standard" | "contained" | "outlined";
    /**
     * Set false to hide pagination input box for page jump
     * @default true
     */
    enablePaginationInput?: boolean;
    /**
     * Set true to enable background color for action buttons. Css variable `--primary-bg` will applied as background.
     * @default false
     */
    isContainedActionButton?: boolean;
    // wrapCellText?: boolean;
  };
  defaultExportConfig?: {
    /**
     * Allows to open print dialog for pdf exported data.
     * @default false
     */
    enablePdfPrint?: boolean;
  };
}

interface ContextProps {
  config?: CustomProperties;
}

const customParameters: CustomProperties = {
  dynamicAccountNumberField: "separated", //for set account number field set
  dynamicAmountSymbol: "INR", //this prop for gridWrapper ,report and formWrapper
  dynamicAmountGroupStyle: "lakh", //this prop for formWrapper
  currencyFormat: "en-IN", //this prop for gridWrapper only
  decimalCount: 2, //this prop for gridWrapper ,report and formWrapper
  commonDateFormat: "dd/MM/yyyy", //this prop for gridWrapper ,report and formWrapper
  commonDateTimeFormat: "dd/MM/yyyy hh:mm:ss aa",
  commonTimeFormat: "hh:mm:ss aa", //this prop for gridWrapper ,report and formWrapper
  denoTableType: "single", //this prop for which type of denomination table you required(type:1.single-->manage receipt and payment both in single column,2.dual:-->manage receipt and payment in saperate column)
  customExtendedTypes: {},
  defaultGridConfig: {
    variant: "standard",
    enablePaginationInput: true,
    isContainedActionButton: false,
    // wrapCellText: false,
  },
  defaultExportConfig: {
    enablePdfPrint: false,
  },
};

const CustomPropertiesConfigurationContext =
  createContext<CustomProperties>(customParameters);

export const CustomPropertiesConfigurationProvider: FC<
  PropsWithChildren<ContextProps>
> = ({ config, children }) => {
  const parameters = useMemo(
    () => ({ ...customParameters, ...config }),
    [config],
  );
  return (
    <CustomPropertiesConfigurationContext.Provider value={parameters}>
      {children}
    </CustomPropertiesConfigurationContext.Provider>
  );
};

export const usePropertiesConfigContext = () => {
  const context = useContext(CustomPropertiesConfigurationContext);
  if (context === undefined) {
    throw new Error(
      "usePropertiesConfigContext must be used within a CustomPropertiesConfigurationProvider",
    );
  }
  return context;
};
